import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Alert } from 'reactstrap';
import Immutable from 'immutable';
import RoadBookingSeatCard from './RoadBookingSeatCard';
import Modal from '../../../common/modal/Modal';
import { roadBookingDeselectSeat, roadBookingSelectSeat } from '../../../../actions';
import RoadBookingPaymentForm from './RoadBookingPaymentForm';
import { DEFAULT_FOOD_OPTION, DEFAULT_VOUCHER_TYPE_OPTION } from '../../../../config/defaults';
import { ROAD_BOOKING_PAYMENT_TYPE } from '../../../../config/constants';
import { clearRoadBookingDiscount, getRoadBookingDiscount, postRoadBookingPay } from '../../../../actions/booking/RoadBooking';
import RoadBookingDiscountCodeForm from './RoadBookingDiscountCodeForm';
import RoadBookingTicketDetails from './RoadBookingTicketDetails';

const RoadBookingSeats = ({ itinerary, dispatchRoadBookingSelectSeat, dispatchRoadBookingDeselectSeat, dispatchPostRoadBookingPay, loading, dispatchGetRoadBookingDiscount, discountCode, dispatchClearRoadBookingDiscount, soldSeats }) => {
  const [showModal, setShowModal] = useState(false);
  const [reservationSeatIdSelected, setReservationSeatIdSelected] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(ROAD_BOOKING_PAYMENT_TYPE.CASH);
  const [seatSelected, setSeatSelected] = useState(null);

  const [loadingSelectSeat, setLoadingSelectSeat] = useState(false);

  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState(null);

  if (loading) {
    return null;
  }

  // Remove seats that are already sold first,
  // then add them back again when rechecking availability.
  // This ensures that if the user searches a different route/destination,
  // previously sold seats are still marked as sold,
  // and if seats are sold in the current location, they are removed and
  // re-added correctly when the seat data refreshes.
  const filterAvailableSeat = (seat) => {
    const isSeat = seat.seatMapElement && seat.seatMapElement.isSeat;
    const isFree = seat.seatStatus === 'FREE';
    const soldSeat = soldSeats.find(s => s.seatId === seat.id);
    return isSeat && isFree && !soldSeat;
  }

  if (!itinerary || !itinerary.seatMapDTO) return null;

  const { seatMapDTO, origin, destination } = itinerary;
  const { floors } = seatMapDTO;

  if (!floors) return null;

  const availableSeats = [];

  floors.forEach(floor => {
    const { seats } = floor;
    if (!seats) return;

    seats.forEach(seat => {
      if (filterAvailableSeat(seat)) {
        availableSeats.push(seat);
      }
    });
  });

  soldSeats.forEach(soldSeat => {
    availableSeats.push({
      id: soldSeat.seatId,
      number: soldSeat.seatNumber,
      seatNumber: soldSeat.seatNumber,
      price: soldSeat.price,
      isSold: true,
    });
  });

  const closeModal = () => {
    setShowModal(false);
    setReservationSeatIdSelected(null);
    setSeatSelected(null);
  }

  const openModal = (seat) => {
    setShowModal(true);
    setSeatSelected(seat);
  }

  const handleSelectSeat = async (seat) => {
    setLoadingSelectSeat(true);

    const reservationPayload = {
      destinationLocationId: destination.id,
      sourceLocationId: origin.id,
      seatId: seat.id,
      itineraryId: itinerary.itinerary.id,
      specialPriceId: seat.specialPriceId || null,
    }

    openModal(seat);

    const reservationResult = await dispatchRoadBookingSelectSeat(reservationPayload);

    if (reservationResult === null) {
      closeModal();
      setLoadingSelectSeat(false);
      return;
    }

    const { id: reservationSeatId } = reservationResult;
    setReservationSeatIdSelected(reservationSeatId);
    setLoadingSelectSeat(false);
  };

  const handleCloseModal = () => {
    if (loadingSelectSeat) return;

    if (reservationSeatIdSelected === null) {
      closeModal();
      return;
    }

    dispatchRoadBookingDeselectSeat({ seatReservationId: reservationSeatIdSelected });
    closeModal();
  };

  const handlePaymentMethodChange = (method) => {
    setSelectedPaymentMethod(method);
  };

  const handleSubmitPaymentForm = async (formValues) => {

    const { passenger, voucherType, phoneNumber, business } = formValues;

    const discountAmount = discountCode.get('maxAmount') || 0;
    const totalAmount = seatSelected.price - discountAmount;

    const newFormValues = {
      passengerId: passenger.id,
      passengerSource: passenger.source,
      paymentMethodId: selectedPaymentMethod.value,
      businessId: business ? business.value : null,
      amount: totalAmount,
      voucherTypeId: voucherType ? voucherType.value : null,
      phoneNumber,
      seatReservationId: reservationSeatIdSelected,
      foodId: DEFAULT_FOOD_OPTION.value, // TODO: Implement
      discountCode: discountCode.get('code') || null,
    }

    if (formValues.businessName) {
      newFormValues.businessCreate = {
        taxId: formValues.businessTaxId,
        name: formValues.businessName,
        addressSummary: formValues.addressSummary,
      }
    }

    const response = await dispatchPostRoadBookingPay(newFormValues);

    if (response === null) {
      handleCloseModal();
      return;
    }

    closeModal();
  }

  const discountCodeSubmit = (values) => {
    if (discountCode.isEmpty() === false) {
      dispatchClearRoadBookingDiscount();
      return;
    }
    const { discountCode: code } = values;
    dispatchGetRoadBookingDiscount({ itineraryId: itinerary.itinerary.id, discountCode: code })
  }

  const handleViewSoldSeat = (seat) => {
    setSelectedSeat(seat);
    setShowTicketModal(true);
  }

  const handleCloseTicketModal = () => {
    setShowTicketModal(false);
    setSelectedSeat(null);
  }

  return (
    <div className='mt-2'>
      <Modal
        title="Seleccionar Asiento"
        body={<RoadBookingPaymentForm onSubmit={handleSubmitPaymentForm} isLoading={loadingSelectSeat} initialValues={{
          voucherType: DEFAULT_VOUCHER_TYPE_OPTION
        }}
          onPaymentMethodChange={handlePaymentMethodChange}
        />}
        show={showModal}
        onClickClose={handleCloseModal}
        onClickCancel={handleCloseModal}
      />
      <Modal
        title="Ver Boleto"
        body={<RoadBookingTicketDetails seat={selectedSeat} />}
        show={showTicketModal}
        onClickConfirm={handleCloseTicketModal}
        confirmButtonText="Cerrar"
        confirmButtonColor="secondary"
      />
      <hr />
      <RoadBookingDiscountCodeForm onSubmit={discountCodeSubmit} />
      <hr />
      <h3>Asientos Disponibles: {availableSeats.length}</h3>
      {discountCode.isEmpty() === false
        && (
          <Alert color="success" className='mt-2'>
            Código de descuento aplicado: S/.{discountCode.get('maxAmount')}
          </Alert>
        )}
      <div className='road-booking card-list'>
        {availableSeats
          .sort((a, b) => a.seatNumber - b.seatNumber)
          .map(seat => (
            <RoadBookingSeatCard key={seat.id} seat={seat} onSelect={handleSelectSeat} onView={handleViewSoldSeat} />
          ))}
      </div>
    </div>
  );
}

RoadBookingSeats.propTypes = {
  itinerary: PropTypes.shape({
    origin: PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
    }).isRequired,
    destination: PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
    }).isRequired,
    seatMapDTO: PropTypes.shape({
      floors: PropTypes.arrayOf(
        PropTypes.shape({
          floorNumber: PropTypes.number.isRequired,
          width: PropTypes.number.isRequired,
          length: PropTypes.number.isRequired,
          seats: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.number.isRequired,
              seatNumber: PropTypes.number.isRequired,
              xPosition: PropTypes.number.isRequired,
              yPosition: PropTypes.number.isRequired,
              price: PropTypes.number.isRequired,
              specialPriceId: PropTypes.string,
              seatMapElement: PropTypes.shape({
                name: PropTypes.string.isRequired,
                elementIcon: PropTypes.string.isRequired,
                isSeat: PropTypes.bool.isRequired,
              }).isRequired,
            })
          ).isRequired,
        })
      ).isRequired
    }).isRequired,
    itinerary: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired,
  }),
  dispatchRoadBookingSelectSeat: PropTypes.func.isRequired,
  dispatchRoadBookingDeselectSeat: PropTypes.func.isRequired,
  dispatchPostRoadBookingPay: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  dispatchGetRoadBookingDiscount: PropTypes.func.isRequired,
  discountCode: PropTypes.instanceOf(Immutable.Map).isRequired,
  dispatchClearRoadBookingDiscount: PropTypes.func.isRequired,
  soldSeats: PropTypes.arrayOf(PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    seatId: PropTypes.number.isRequired,
    seatNumber: PropTypes.number.isRequired,
    itineraryId: PropTypes.number.isRequired,
  })).isRequired,
};

RoadBookingSeats.defaultProps = {
  itinerary: null,
};

const mapStateToProps = ({ BookingUnit }) => {
  const itinerary = BookingUnit.Booking.getIn(['roadBooking', 'itinerary']);
  const loading = !BookingUnit.Booking.getIn(['roadBooking', 'activity']).isEmpty();
  const discountCode = BookingUnit.Booking.getIn(['roadBooking', 'discount']);
  const soldSeatsStore = BookingUnit.Booking.getIn(['roadBooking', 'soldSeats']);
  const soldSeats = soldSeatsStore ? soldSeatsStore.toJS() : [];
  return {
    itinerary: itinerary ? itinerary.toJS() : null,
    loading,
    discountCode,
    soldSeats,
  };
};

const mapDispatchToProps = {
  dispatchRoadBookingSelectSeat: roadBookingSelectSeat,
  dispatchRoadBookingDeselectSeat: roadBookingDeselectSeat,
  dispatchPostRoadBookingPay: postRoadBookingPay,
  dispatchGetRoadBookingDiscount: getRoadBookingDiscount,
  dispatchClearRoadBookingDiscount: clearRoadBookingDiscount,
}

export default connect(mapStateToProps, mapDispatchToProps)(RoadBookingSeats);
